import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { marked } from 'marked';

import { ApiService } from '../../../../core/services/api.service';
import { ParsedFileHighlightPage } from '../../../../core/models/file-browser.models';

@Component({
  selector: 'app-review-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './review-dialog.component.html',
})
export class ReviewDialogComponent implements OnInit {
  @Input() parsedS3Key!: string;
  @Input() versionId: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<string>();

  private api = inject(ApiService);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  // Loading state
  loading = true;
  loadError: string | null = null;

  // Content
  markdownContent = '';
  previewMode = false;

  // Highlight pages
  pages: ParsedFileHighlightPage[] = [];
  totalPages = 0;
  currentPage = 1;

  // Save state
  saving = false;
  saveError: string | null = null;

  ngOnInit(): void {
    forkJoin({
      contentUrl: this.api.getParsedFileContentUrl(this.parsedS3Key, this.versionId),
      highlights: this.api.getParsedFileHighlights(this.parsedS3Key),
    }).subscribe({
      next: ({ contentUrl, highlights }) => {
        this.pages = highlights.pages;
        this.totalPages = highlights.totalPages;
        this.currentPage = 1;
        // Fetch the actual markdown text via the presigned URL
        this.http.get(contentUrl, { responseType: 'text' }).subscribe({
          next: text => {
            this.markdownContent = text;
            this.loading = false;
            this.cdr.markForCheck();
          },
          error: err => {
            this.loadError = err?.message || 'Failed to load file content';
            this.loading = false;
            this.cdr.markForCheck();
          },
        });
      },
      error: err => {
        this.loadError = err?.message || 'Failed to load review data';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  get currentPageUrl(): string | null {
    const page = this.pages.find(p => p.pageNum === this.currentPage);
    return page?.url ?? null;
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.cdr.markForCheck();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.cdr.markForCheck();
    }
  }

  goToPage(n: number): void {
    if (n >= 1 && n <= this.totalPages) {
      this.currentPage = n;
      this.cdr.markForCheck();
    }
  }

  saveApproved(): void {
    if (this.saving) return;
    this.saving = true;
    this.saveError = null;
    this.cdr.markForCheck();

    this.api.saveParsedRevision(this.parsedS3Key, this.markdownContent).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(this.parsedS3Key);
      },
      error: err => {
        this.saveError = err?.message || 'Failed to save revision';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  togglePreview(): void {
    this.previewMode = !this.previewMode;
    this.cdr.markForCheck();
  }

  get markedHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(marked(this.markdownContent) as string);
  }

  fileName(): string {
    /* c8 ignore next */
    return this.parsedS3Key.split('/').pop() ?? this.parsedS3Key;
  }

  thumbnailPages(): ParsedFileHighlightPage[] {
    return this.pages.slice(0, 20);
  }
}
